import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { ROLE_LABELS, TEAM_FILTERS, type AdminTeamDetail, type AdminTeamRow, type Page, type TeamFilter } from '@hoopify/shared';
import { api, ApiError, errorMessage } from '../../lib/api';
import { TEAM_FILTER_LABELS } from '../../lib/admin';
import { formatDate, timeAgo } from '../../lib/format';
import { Alert, Button, Field, LoadingBlock } from '../../components/ui';
import { Pager, ReasonDialog, Stat } from '../../components/admin/AdminLayout';
import { Flag, LogList, SearchBar, useListParams } from './AdminAccountsPage';

export function AdminTeamsPage() {
  const { q, filter, page, text, setText, set } = useListParams('active');
  const f = (TEAM_FILTERS as readonly string[]).includes(filter) ? (filter as TeamFilter) : 'active';
  const list = useQuery({
    queryKey: ['admin', 'teams', q, f, page],
    queryFn: () => api.get<Page<AdminTeamRow>>(`/admin/teams?q=${encodeURIComponent(q)}&filter=${f}&page=${page}`),
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <h1 className="mb-5 text-[24px] font-semibold tracking-[-0.01em]">Teams</h1>
      <SearchBar text={text} setText={setText} placeholder="Search by team or head coach" filter={f} options={TEAM_FILTER_LABELS} onFilter={(v) => set('filter', v === 'active' ? null : v)} />
      {list.isError && <Alert>{errorMessage(list.error)}</Alert>}
      {list.isLoading ? (
        <LoadingBlock />
      ) : list.data && (
        <>
          <p className="tnum mb-2 text-[14px] text-muted">
            {list.data.total} team{list.data.total === 1 ? '' : 's'}
          </p>
          <div className="overflow-x-auto rounded-lg border border-line bg-surface">
            <table className="w-full min-w-[720px] text-left text-[14px]">
              <thead className="border-b border-line text-[13px] text-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">Team</th>
                  <th className="px-4 py-2 font-medium">Head coach</th>
                  <th className="px-4 py-2 text-right font-medium">Members</th>
                  <th className="px-4 py-2 text-right font-medium">Sessions</th>
                  <th className="px-4 py-2 font-medium">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {list.data.rows.map((t) => (
                  <tr key={t.id} className="hover:bg-sunken">
                    <td className="px-4 py-2.5">
                      <Link to={`/admin/teams/${t.id}`} className="font-medium hover:underline">
                        {t.name}
                      </Link>
                      {t.ageGroup && <span className="text-muted"> · {t.ageGroup}</span>}
                      {t.archivedAt && (
                        <span className="ml-2">
                          <Flag tone="muted">Archived</Flag>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {t.headCoach ? (
                        <Link to={`/admin/accounts/${t.headCoach.id}`} className="hover:underline">
                          {t.headCoach.name}
                        </Link>
                      ) : (
                        <span className="text-warn">No head coach</span>
                      )}
                    </td>
                    <td className="tnum px-4 py-2.5 text-right">{t.memberCount}</td>
                    <td className="tnum px-4 py-2.5 text-right">{t.sessionCount}</td>
                    <td className="tnum px-4 py-2.5 whitespace-nowrap text-muted">{formatDate(t.createdAt)}</td>
                  </tr>
                ))}
                {!list.data.rows.length && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted">
                      No teams match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pager page={page} total={list.data.total} pageSize={list.data.pageSize} onPage={(p) => set('page', String(p))} />
        </>
      )}
    </>
  );
}

export function AdminTeamPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const key = ['admin', 'team', id];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<AdminTeamDetail>(`/admin/teams/${id}`) });
  const [action, setAction] = useState<'archive' | 'restore' | 'transfer' | null>(null);
  const [email, setEmail] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [transferError, setTransferError] = useState<string | undefined>();

  const post = (path: string, body: unknown) =>
    api.post<AdminTeamDetail>(`/admin/teams/${id}/${path}`, body).then((d) => {
      qc.setQueryData(key, d);
      void qc.invalidateQueries({ queryKey: ['admin', 'teams'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'account'] });
      return d;
    });

  if (q.isLoading) return <LoadingBlock />;
  if (!q.data) return <Alert>{errorMessage(q.error)}</Alert>;
  const t = q.data;

  return (
    <>
      <Link to="/admin/teams" className="mb-4 inline-flex items-center gap-1.5 text-[14px] text-muted hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> Teams
      </Link>
      <div className="mb-5 flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-[24px] leading-tight sm:text-[28px]">
            {t.name}
            {t.archivedAt && (
              <span className="ml-2 align-middle">
                <Flag tone="muted">Archived {formatDate(t.archivedAt)}</Flag>
              </span>
            )}
          </h1>
          <p className="text-muted">
            {t.ageGroup ? `${t.ageGroup} · ` : ''}Created {formatDate(t.createdAt)}
            {t.lastActivityAt && ` · last activity ${timeAgo(t.lastActivityAt)}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => { setEmail(''); setTransferError(undefined); setAction('transfer'); }}>
            Move to another head coach
          </Button>
          {t.archivedAt ? (
            <Button variant="secondary" disabled={!t.headCoach} title={t.headCoach ? undefined : 'Move it to a head coach to bring it back'} onClick={() => setAction('restore')}>
              Restore
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => setAction('archive')}>
              Archive
            </Button>
          )}
        </div>
      </div>
      {notice && (
        <div className="mb-4">
          <Alert tone="ok">{notice}</Alert>
        </div>
      )}
      {!t.headCoach && (
        <div className="mb-4">
          <Alert tone="warn">This team has no head coach (their account was deleted). Move it to a coach to bring it back.</Alert>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat label="Sessions" value={t.counts.sessions} sub={`${t.counts.published} published`} />
        <Stat label="Members" value={t.memberCount} />
        <Stat label="Roster players" value={t.counts.rosterPlayers} />
        <Stat label="Seasons" value={t.counts.seasons} />
        <Stat label="Drills shared" value={t.counts.drillsShared} />
      </div>
      <p className="mt-2 text-[13px] text-muted">Session plans, notes, rosters and attendance stay private to the team. Admins only see counts.</p>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <section>
          <h2 className="mb-2 text-[16px] font-semibold">Members</h2>
          <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
            {t.members.map((m) => (
              <li key={m.userId}>
                <Link to={`/admin/accounts/${m.userId}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-sunken">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{m.name}</p>
                    <p className="truncate text-[13px] text-muted">{m.email}</p>
                  </div>
                  <span className="text-[13px] text-muted">{ROLE_LABELS[m.role]}</span>
                </Link>
              </li>
            ))}
            {!t.members.length && <li className="px-4 py-6 text-center text-muted">Nobody is on this team.</li>}
          </ul>
        </section>
        <section>
          <h2 className="mb-2 text-[16px] font-semibold">Admin history</h2>
          <LogList rows={t.log} empty="No admin actions on this team yet." />
        </section>
      </div>

      <ReasonDialog open={action === 'archive'} onClose={() => setAction(null)} title={`Archive ${t.name}?`} confirm="Archive" tone="danger" run={(reason) => post('archive', { reason }).then(() => setNotice('Team archived. The head coach has been emailed.'))}>
        It’s hidden for everyone on it, but nothing is deleted. You can restore it any time. The head coach gets an email with the reason, and it shows in the team’s activity log.
      </ReasonDialog>
      <ReasonDialog open={action === 'restore'} onClose={() => setAction(null)} title={`Restore ${t.name}?`} confirm="Restore" run={(reason) => post('restore', { reason }).then(() => setNotice('Team restored. The head coach has been emailed.'))}>
        Everyone on it will see it again.
      </ReasonDialog>
      <ReasonDialog
        open={action === 'transfer'}
        onClose={() => setAction(null)}
        title={`Move ${t.name} to another head coach`}
        confirm="Make head coach"
        run={(reason) =>
          post('transfer', { email, reason })
            .then(() => setNotice(`Done. They’re head coach now, and both coaches have been emailed.`))
            .catch((e) => {
              if (e instanceof ApiError && e.fields.email) setTransferError(e.fields.email);
              throw e;
            })
        }
        extra={<Field label="New head coach’s email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setTransferError(undefined); }} error={transferError} hint="They need a Hoopify account already." autoComplete="off" />}
      >
        {t.headCoach ? <>{t.headCoach.name} stays on the team as an assistant coach.</> : 'The team will be restored with them as head coach.'} The change shows in the team’s activity log.
      </ReasonDialog>
    </>
  );
}
